import type { RankedPromptChoice } from './parseRankedAiPromptList';
import { isValidPromptLine } from './parseAiPromptList';
import { tierFromIndexForBadge } from './promptRecommendationBadge';

export const ROLE_PROMPTS_STORAGE_KEY = 'skipscreen_role_specific_prompts';
const ROLE_PROMPTS_CACHE_VERSION = 2;

export const buildRolePromptsCacheKey = (jobTitle: string, jobIndustry: string, level: string) =>
  `${jobTitle}|${jobIndustry}|${level}`;

type CachedRolePrompts = {
  version?: number;
  cacheKey?: string;
  prompts?: unknown[];
};

function normalizeCachedEntry(item: unknown, index: number): RankedPromptChoice | null {
  if (typeof item === 'string' && item.trim()) {
    const text = item.trim();
    if (!isValidPromptLine(text)) return null;
    return { text, tier: tierFromIndexForBadge(index) };
  }
  if (item && typeof item === 'object') {
    const row = item as { text?: string; tier?: string | null };
    const text = row.text?.trim();
    if (!text || !isValidPromptLine(text)) return null;
    const tier = row.tier === 'most' || row.tier === 'recommended' ? row.tier : null;
    return {
      text,
      tier: tier ?? tierFromIndexForBadge(index),
    };
  }
  return null;
}

function readRaw(): CachedRolePrompts | null {
  for (const storage of [localStorage, sessionStorage]) {
    try {
      const raw = storage.getItem(ROLE_PROMPTS_STORAGE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as CachedRolePrompts;
      if (parsed?.prompts?.length) return parsed;
    } catch {
      storage.removeItem(ROLE_PROMPTS_STORAGE_KEY);
    }
  }
  return null;
}

export const loadCachedRolePrompts = (
  jobTitle: string,
  jobIndustry: string,
  level: string
): RankedPromptChoice[] => {
  const cacheKey = buildRolePromptsCacheKey(jobTitle, jobIndustry, level);
  const cached = readRaw();

  if (
    cached?.version !== ROLE_PROMPTS_CACHE_VERSION ||
    cached?.cacheKey !== cacheKey ||
    !Array.isArray(cached.prompts)
  ) {
    return [];
  }

  return cached.prompts
    .map(normalizeCachedEntry)
    .filter((p): p is RankedPromptChoice => p !== null);
};

export const saveCachedRolePrompts = (
  jobTitle: string,
  jobIndustry: string,
  level: string,
  prompts: RankedPromptChoice[]
) => {
  const payload = JSON.stringify({
    version: ROLE_PROMPTS_CACHE_VERSION,
    cacheKey: buildRolePromptsCacheKey(jobTitle, jobIndustry, level),
    prompts,
  });
  localStorage.setItem(ROLE_PROMPTS_STORAGE_KEY, payload);
  sessionStorage.setItem(ROLE_PROMPTS_STORAGE_KEY, payload);
};
